import { cloneElement, FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Text, VStack } from '@bonusly/design-system';

import { SmartNavLink } from './SmartNavLink';
import { TopLevelNavItem } from './TopLevelNavItem';
import { NavSectionConfig } from './types/nav';

interface NavSectionProps {
	section: NavSectionConfig;
	onNavLinkClick?: () => void;
}

export const NavSection: FC<NavSectionProps> = ({ section, onNavLinkClick }) => {
	const { t } = useTranslation('vertical_navbar');

	return (
		<TopLevelNavItem
			badgeCount={section.badgeCount}
			showAlertDot={section.showAlertDot}
			icon={cloneElement(<section.icon />, {
				color: 'secondary.fg',
				size: 'md',
				mt: 1.5,
			})}
			label={t(section.title as any)}
		>
			<VStack data-testid={section.id}>
				{/* Render direct links first */}
				{section.links?.map((link, index) => (
					<SmartNavLink
						badgeCount={link.badgeCount}
						key={`link-${index}`}
						label={typeof link.label === 'string' ? t(link.label) : link.label}
						matchActive={link.matchActive}
						showAlertDot={link.showAlertDot}
						locked={link.locked}
						lockedTooltip={t('admin_menu.reports.locked_tooltip')}
						url={link.url}
						isRecognizeRoute={link.isRecognizeRoute}
						external={link.external}
						onClick={onNavLinkClick}
					/>
				))}

				{/* Render subsections */}
				{section.subsections?.map((subsection, subsectionIndex) => (
					<VStack key={`subsection-${subsectionIndex}`} w="full" spacing={2}>
						{/* Subsection header */}
						<Text color="text.softer" fontSize={1} fontWeight="medium" textAlign="left" w="full" px={2}>
							{t(subsection.label as any)}
						</Text>

						{/* Subsection links */}
						{subsection.links.map((link, linkIndex) => (
							<SmartNavLink
								badgeCount={link.badgeCount}
								key={`subsection-${subsectionIndex}-link-${linkIndex}`}
								label={typeof link.label === 'string' ? t(link.label) : link.label}
								matchActive={link.matchActive}
								showAlertDot={link.showAlertDot}
								locked={link.locked}
								lockedTooltip={t('admin_menu.reports.locked_tooltip')}
								url={link.url}
								isRecognizeRoute={link.isRecognizeRoute}
								external={link.external}
								onClick={onNavLinkClick}
							/>
						))}
					</VStack>
				))}
			</VStack>
		</TopLevelNavItem>
	);
};
